import React, { useState, useRef, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

interface Message {
    id: string;
    role: 'user' | 'assistant';
    content: string;
}

export const SupportWidget: React.FC = () => {
    const { t, i18n } = useTranslation();
    const location = useLocation();
    const [isOpen, setIsOpen] = useState(false);
    const [messages, setMessages] = useState<Message[]>([]);
    const [inputText, setInputText] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const messagesEndRef = useRef<HTMLDivElement>(null);

    // Hide on chat page to avoid overlapping with tutor chat input
    const isChatPage = location.pathname === '/chat';

    // Initialize/Update welcome message when language changes
    useEffect(() => {
        if (messages.length === 0) {
            setMessages([
                {
                    id: 'welcome',
                    role: 'assistant',
                    content: t('support.welcome', "👋 Hi! I'm TutorAI Support. How can I help you today?")
                }
            ]);
        }
        // messages.length is included per exhaustive-deps; since the effect only calls setMessages
        // when messages.length === 0, once it fires the length becomes 1 and this guard prevents
        // any further re-initialization, so no loop is introduced.
    }, [t, messages.length]);

    const toggleChat = () => setIsOpen(!isOpen);

    useEffect(() => {
        if (isOpen) {
            scrollToBottom();
        }
    }, [messages, isOpen]);

    // Permite abrir este chat desde cualquier otra parte de la app (ej. el boton "Contactar
    // Soporte" en Mi Perfil) sin tener que levantar el estado isOpen hasta App.tsx. El widget
    // vive montado una sola vez en la raiz (ver App.tsx) y el proyecto no tiene un gestor de
    // estado global, asi que un CustomEvent de window es la forma mas simple de comunicarse con
    // el sin prop drilling.
    useEffect(() => {
        const handleOpenRequest = () => setIsOpen(true);
        window.addEventListener('tutoria:open-support-chat', handleOpenRequest);
        return () => window.removeEventListener('tutoria:open-support-chat', handleOpenRequest);
    }, []);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    const handleSendMessage = async () => {
        if (!inputText.trim() || isLoading) return;

        const userMsg: Message = { id: Date.now().toString(), role: 'user', content: inputText };
        setMessages(prev => [...prev, userMsg]);
        setInputText('');
        setIsLoading(true);

        try {
            const { data: { session } } = await supabase.auth.getSession();
            const token = session?.access_token || import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;

            const functionUrl = `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/support-chat`;

            const response = await fetch(functionUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({
                    message: userMsg.content,
                    conversationHistory: messages.map(m => ({ role: m.role === 'user' ? 'user' : 'ai', content: m.content })),
                    language: i18n.language // Send current language
                })
            });

            if (!response.ok) throw new Error('Network response was not ok');

            const data = await response.json();

            const aiMsg: Message = {
                id: (Date.now() + 1).toString(),
                role: 'assistant',
                content: data.response || t('support.error', "Sorry, I'm having trouble connecting right now.")
            };

            setMessages(prev => [...prev, aiMsg]);

        } catch (error) {
            console.error('Support chat error:', error);
            setMessages(prev => [...prev, {
                id: Date.now().toString(),
                role: 'assistant',
                content: t('support.connectionError', "I'm having a little trouble connecting. Please check your internet. 🔌")
            }]);
        } finally {
            setIsLoading(false);
        }
    };

    if (isChatPage) return null;

    return (
        <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end pointer-events-none">
            {/* Chat Window */}
            <div
                className={`pointer-events-auto bg-white rounded-2xl shadow-2xl w-80 sm:w-96 mb-4 overflow-hidden transition-all duration-300 origin-bottom-right transform border border-gray-100 ${isOpen ? 'scale-100 opacity-100 translate-y-0' : 'scale-90 opacity-0 translate-y-10 pointer-events-none h-0'
                    }`}
            >
                {/* Header */}
                <div className="bg-gradient-to-r from-purple-600 to-indigo-600 p-4 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center backdrop-blur-sm">
                            <i className="ri-customer-service-2-fill text-white"></i>
                        </div>
                        <div>
                            <h3 className="text-white font-bold text-sm">{t('support.title', 'TutorAI Support')}</h3>
                            <p className="text-purple-200 text-xs">{t('support.subtitle', 'We typically reply instantly')}</p>
                        </div>
                    </div>
                    <button onClick={toggleChat} className="text-white/80 hover:text-white transition-colors">
                        <i className="ri-close-line text-xl"></i>
                    </button>
                </div>

                {/* Messages */}
                <div className="h-80 overflow-y-auto p-4 bg-gray-50 space-y-3">
                    {messages.map((msg) => (
                        <div key={msg.id} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                            <div
                                className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm shadow-sm ${msg.role === 'user'
                                    ? 'bg-purple-600 text-white rounded-br-none'
                                    : 'bg-white text-gray-700 border border-gray-100 rounded-bl-none'
                                    }`}
                            >
                                {msg.content}
                            </div>
                        </div>
                    ))}
                    {isLoading && (
                        <div className="flex justify-start">
                            <div className="bg-white rounded-2xl rounded-bl-none px-3 py-2 border border-gray-100 shadow-sm">
                                <div className="flex gap-1">
                                    <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                                    <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                                    <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                                </div>
                            </div>
                        </div>
                    )}
                    <div ref={messagesEndRef} />
                </div>

                {/* Input */}
                <div className="p-3 bg-white border-t border-gray-100">
                    <div className="flex items-center gap-2 bg-gray-50 rounded-full px-4 py-2 border border-gray-200 focus-within:border-purple-300 focus-within:ring-2 focus-within:ring-purple-100 transition-all">
                        <input
                            type="text"
                            value={inputText}
                            onChange={(e) => setInputText(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && handleSendMessage()}
                            placeholder={t('support.placeholder', 'Ask for help...')}
                            className="flex-1 bg-transparent border-none outline-none text-sm text-gray-700 placeholder-gray-400"
                            disabled={isLoading}
                        />
                        <button
                            onClick={handleSendMessage}
                            disabled={!inputText.trim() || isLoading}
                            className="text-purple-600 hover:text-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                            <i className="ri-send-plane-fill text-lg"></i>
                        </button>
                    </div>
                    <div className="text-center mt-2">
                        <p className="text-[10px] text-gray-400">Powered by TutorAI</p>
                    </div>
                </div>
            </div>

            {/* Launcher Button */}
            <button
                onClick={toggleChat}
                className={`pointer-events-auto w-14 h-14 rounded-full shadow-lg flex items-center justify-center transition-all duration-300 transform hover:scale-110 ${isOpen ? 'bg-gray-200 text-gray-600 rotate-90' : 'bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:shadow-purple-500/30'
                    }`}
            >
                {isOpen ? <i className="ri-close-line text-2xl"></i> : <i className="ri-question-answer-line text-2xl"></i>}
            </button>
        </div>
    );
};
